import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, CheckCircle2, Clock, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";

/**
 * Public Staff Applications landing page.
 * Lists all currently-open positions. If nothing is open, shows the
 * "applications are currently closed" state instead of the form CTA.
 */
export default function ApplyIndex() {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/positions");
        setPositions(data.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16" data-testid="apply-page">
      <SEO
        title="Staff Applications · Serverlistr"
        description="Join the Serverlistr team — apply for a staff position and help build the largest Discord server directory."
        canonical="/apply"
        noindex={positions.length === 0}
      />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blurple/10 border border-blurple/30 text-xs font-bold text-blurple tracking-widest uppercase">
          <Briefcase className="w-3 h-3" />
          Join the team
        </div>
        <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">
          Staff Applications
        </h1>
        <p className="mt-4 text-zinc-400 text-base sm:text-lg max-w-2xl">
          Serverlistr is looking for passionate, community-minded people to help us build
          the best Discord server directory on the internet. All roles are volunteer.
        </p>
      </motion.div>

      {loading ? (
        <div className="mt-10 text-zinc-500">Loading positions…</div>
      ) : positions.length === 0 ? (
        <div
          data-testid="apply-closed"
          className="mt-10 rounded-2xl border border-white/10 bg-[#0A0A0F] p-8 text-center"
        >
          <Clock className="w-8 h-8 mx-auto text-zinc-500" />
          <h2 className="mt-3 font-heading text-xl font-bold">
            Staff applications are currently closed
          </h2>
          <p className="mt-2 text-zinc-400">
            Check back soon — we open recruitment when specific roles need filling.
            Follow us on Discord to hear about openings first.
          </p>
        </div>
      ) : (
        <div className="mt-10 grid gap-5">
          {positions.map((p) => (
            <PositionCard key={p.id} p={p} />
          ))}
        </div>
      )}

      <div className="mt-10 rounded-2xl border border-white/5 bg-white/[0.02] p-5 text-sm text-zinc-400">
        <div className="flex items-center gap-2 text-white font-semibold">
          <ShieldCheck className="w-4 h-4 text-neon-cyan" /> A note before you apply
        </div>
        <p className="mt-2 leading-relaxed">
          Applications are reviewed by the Serverlistr Owner personally. Once your app is
          moved to <b>Under Review</b>, you can no longer withdraw it. Please be honest —
          we cross-check Discord accounts against the site.
        </p>
      </div>
    </div>
  );
}

function PositionCard({ p }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="position-card"
      data-position-id={p.id}
      className="rounded-2xl border border-white/10 bg-[#0A0A0F] p-6 hover:border-blurple/40 transition-colors"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading text-xl font-black">{p.name}</h3>
          <p className="mt-1 text-zinc-400 text-sm">{p.description}</p>
        </div>
        <Link
          to={`/apply/${p.id}`}
          data-testid="apply-cta"
          className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-blurple hover:bg-blurple-hover text-white text-sm font-bold transition-all active:scale-95 shadow-lg shadow-blurple/20"
        >
          Apply →
        </Link>
      </div>
      {(p.responsibilities?.length || p.requirements?.length) && (
        <div className="mt-5 grid sm:grid-cols-2 gap-5">
          {p.responsibilities?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-zinc-500 mb-2">
                Responsibilities
              </div>
              <ul className="space-y-1 text-sm text-zinc-300">
                {p.responsibilities.map((r, i) => (
                  <li key={i} className="flex gap-2">
                    <CheckCircle2 className="w-4 h-4 text-neon-cyan mt-0.5 shrink-0" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {p.requirements?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-zinc-500 mb-2">
                Requirements
              </div>
              <ul className="space-y-1 text-sm text-zinc-300">
                {p.requirements.map((r, i) => (
                  <li key={i} className="flex gap-2">
                    <CheckCircle2 className="w-4 h-4 text-neon-pink mt-0.5 shrink-0" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
